export const DESIGN_STORAGE_KEY = "konnectfbr_invoice_design_v1";
export const DESIGN_CANVAS_WIDTH = 560;
export const DESIGN_CANVAS_HEIGHT = 792;
export const PRINT_PAGE_WIDTH_MM = 194;
export const PRINT_PAGE_HEIGHT_MM = 277;

const clampDesignValue = (value, min, max) => Math.min(Math.max(value, min), max);

export const FBR_ITEM_FIELD_OPTIONS = [
  { key: "hsCode", label: "HS Code" },
  { key: "productDescription", label: "Description" },
  { key: "rate", label: "Rate of tax" },
  { key: "uoM", label: "UoM" },
  { key: "quantity", label: "Quantity" },
  { key: "totalValues", label: "Total Values" },
  { key: "valueSalesExcludingST", label: "Value excluding tax" },
  { key: "fixedNotifiedValueOrRetailPrice", label: "Price per unit" },
  { key: "salesTaxApplicable", label: "Sales Tax" },
  { key: "salesTaxWithheldAtSource", label: "Sales Tax Withheld" },
  { key: "extraTax", label: "Extra Tax" },
  { key: "furtherTax", label: "Further Tax" },
  { key: "sroScheduleNo", label: "SRO Schedule No" },
  { key: "fedPayable", label: "FED Payable" },
  { key: "discount", label: "Discount" },
  { key: "saleType", label: "Sale Type" },
  { key: "sroItemSerialNo", label: "SRO Item Serial No" },
  { key: "validation", label: "Validation" },
  { key: "remarks", label: "Remarks" },
  { key: "invoiceNumber", label: "Invoice Number" },
];

export const DERIVED_ITEM_FIELD_OPTIONS = [
  { key: "sr", label: "Sr." },
  { key: "unitPrice", label: "Price per unit" },
  { key: "lineTotal", label: "Total Price" },
  { key: "totalValueIncludingSalesTax", label: "Total Value including sales tax" },
];

export const INVOICE_ITEM_FIELD_OPTIONS = [
  DERIVED_ITEM_FIELD_OPTIONS[0],
  ...FBR_ITEM_FIELD_OPTIONS,
  ...DERIVED_ITEM_FIELD_OPTIONS.slice(1),
];

export const DEFAULT_ITEM_COLUMNS = [
  "sr",
  "hsCode",
  "productDescription",
  "quantity",
  "unitPrice",
  "lineTotal",
  "valueSalesExcludingST",
  "rate",
  "salesTaxApplicable",
  "discount",
  "totalValueIncludingSalesTax",
];

export const DEFAULT_BREAKDOWN_ROWS = [
  { id: "salesBeforeDiscount", label: "Sales Amount (Excl. Tax) Before Discount", visible: true, valueType: "computed" },
  { id: "totalExcl", label: "Sales Amount (Excl. Tax)", visible: true, valueType: "computed" },
  { id: "discount", label: "Discount", visible: true, valueType: "computed" },
  { id: "totalTax", label: "Sales Tax", visible: true, valueType: "computed" },
  { id: "furtherTax", label: "Further Tax", visible: false, valueType: "computed" },
  { id: "extraTax", label: "Extra Tax", visible: false, valueType: "computed" },
  { id: "fedPayable", label: "FED Payable", visible: false, valueType: "computed" },
  { id: "salesTaxWithheldAtSource", label: "Sales Tax Withheld At Source", visible: false, valueType: "computed" },
  { id: "incomeTax236G236H", label: "236G / 236H", visible: true, valueType: "computed" },
  { id: "incomeTaxAdd", label: "Income Tax (Adv)", visible: true, valueType: "computed" },
  { id: "grossInvoiceValue", label: "Gross Invoice Value", visible: true, valueType: "computed", isNet: true },
  { id: "netPayableAmount", label: "Net Payable Amount", visible: true, valueType: "computed", isNet: true },
  { id: "amountWords", label: "Net Payable Amount (In Words)", visible: true, valueType: "words" },
];

export const defaultInvoiceDesignTemplate = {
  logo: "",
  letterhead: "",
  letterheadTop: 150,
  sellerContactEmail: "",
  sellerContactPhone: "",
  itemColumns: DEFAULT_ITEM_COLUMNS.map((key) => ({ key, visible: true })),
  customItemColumns: [],
  breakdownRows: DEFAULT_BREAKDOWN_ROWS,
  blocks: [
    { id: "company_logo", label: "Company Logo", visible: true, x: 214, y: 34, w: 132, h: 98 },
    { id: "seller_contact", label: "Seller Contact", visible: true, x: 24, y: 82, w: 130, h: 42 },
    { id: "invoice_title", label: "Invoice Title", visible: true, x: 24, y: 76, w: 176, h: 34 },
    { id: "invoice_meta", label: "Serial / Date / Time", visible: true, x: 24, y: 113, w: 512, h: 24 },
    { id: "seller_info", label: "Seller Information", visible: true, x: 24, y: 140, w: 250, h: 70 },
    { id: "buyer_info", label: "Buyer Information", visible: true, x: 285, y: 140, w: 250, h: 60 },
    { id: "description_code", label: "Description Code", visible: true, x: 365, y: 206, w: 170, h: 18 },
    { id: "items_table", label: "Items Table", visible: true, x: 24, y: 228, w: 512, h: 62 },
    { id: "fbr_invoice", label: "FBR Invoice Number", visible: true, x: 24, y: 296, w: 260, h: 18 },
    { id: "verification", label: "FBR Logo & QR Code", visible: true, x: 24, y: 354, w: 220, h: 92 },
    { id: "totals", label: "Total Breakdown", visible: true, x: 380, y: 406, w: 155, h: 40 },
    { id: "amount_words", label: "Amount in Words", visible: true, x: 380, y: 452, w: 155, h: 36 },
    { id: "signature", label: "Signature", visible: true, x: 24, y: 486, w: 280, h: 44 },
    { id: "invoice_note", label: "Invoice Note", visible: true, x: 26, y: 548, w: 280, h: 98 },
    { id: "additional_info", label: "Additional Information", visible: true, x: 26, y: 668, w: 280, h: 92 },
  ],
};

export const normalizeInvoiceDesignTemplate = (template = {}) => {
  const savedBlocks = Array.isArray(template.blocks) ? template.blocks : [];
  const savedColumns = Array.isArray(template.itemColumns) ? template.itemColumns : [];
  const legacyDefaultColumns = savedColumns.length === 0;
  const knownColumnKeys = new Set(INVOICE_ITEM_FIELD_OPTIONS.map((field) => field.key));
  const itemColumns = INVOICE_ITEM_FIELD_OPTIONS.map((field) => {
    const savedColumn = savedColumns.find((column) => column.key === field.key);
    return {
      key: field.key,
      visible: savedColumn ? savedColumn.visible !== false : DEFAULT_ITEM_COLUMNS.includes(field.key),
    };
  }).filter((column) => knownColumnKeys.has(column.key));
  const customItemColumns = Array.isArray(template.customItemColumns)
    ? template.customItemColumns
        .map((column, index) => ({
          id: column.id || `custom_item_${index + 1}`,
          label: column.label || `Custom ${index + 1}`,
          value: column.value || "",
          visible: column.visible !== false,
        }))
        .filter((column) => column.label.trim())
    : [];
  const savedBreakdownRows = Array.isArray(template.breakdownRows) ? template.breakdownRows : [];
  const amountWordsBlock = savedBlocks.find((block) => block.id === "amount_words");
  const breakdownRows = DEFAULT_BREAKDOWN_ROWS.map((defaultRow) => {
    const savedRow = savedBreakdownRows.find((row) => row.id === defaultRow.id);
    if (defaultRow.id === "amountWords" && !savedRow && amountWordsBlock) {
      return { ...defaultRow, visible: amountWordsBlock.visible !== false };
    }
    return savedRow ? { ...defaultRow, visible: savedRow.visible !== false } : defaultRow;
  });
  const savedCustomBreakdownRows = savedBreakdownRows
    .filter((row) => !DEFAULT_BREAKDOWN_ROWS.some((defaultRow) => defaultRow.id === row.id))
    .map((row, index) => ({
      id: row.id || `custom_breakdown_${index + 1}`,
      label: row.label || `Custom ${index + 1}`,
      value: row.value || "",
      visible: row.visible !== false,
      valueType: "custom",
    }))
    .filter((row) => row.label.trim());
  const blocks = defaultInvoiceDesignTemplate.blocks.map((defaultBlock) => {
    const savedBlock = savedBlocks.find((block) => block.id === defaultBlock.id);
    const mergedBlock = savedBlock ? { ...defaultBlock, ...savedBlock } : defaultBlock;
    if (mergedBlock.id === "company_logo") {
      const width = Math.max(Number(mergedBlock.w) || defaultBlock.w, defaultBlock.w);
      const height = Math.max(Number(mergedBlock.h) || defaultBlock.h, defaultBlock.h);
      const savedX = Number(savedBlock?.x);
      const savedY = Number(savedBlock?.y);
      const hasUserPosition = Boolean(savedBlock?.userPositioned) || (savedBlock && Number.isFinite(savedX) && savedX !== 42 && savedX !== 238);
      return {
        ...mergedBlock,
        w: width,
        h: height,
        x: hasUserPosition ? clampDesignValue(savedX, 0, DESIGN_CANVAS_WIDTH - width) : Math.round((DESIGN_CANVAS_WIDTH - width) / 2),
        y: hasUserPosition && Number.isFinite(savedY) ? clampDesignValue(savedY, 0, DESIGN_CANVAS_HEIGHT - height) : mergedBlock.y,
      };
    }
    if (mergedBlock.id === "invoice_title") {
      return {
        ...mergedBlock,
        w: Math.max(Number(mergedBlock.w) || 0, defaultBlock.w),
        h: Math.max(Number(mergedBlock.h) || 0, defaultBlock.h),
      };
    }
    return mergedBlock;
  });

  const normalized = {
    ...defaultInvoiceDesignTemplate,
    ...template,
    itemColumns: legacyDefaultColumns ? defaultInvoiceDesignTemplate.itemColumns : itemColumns,
    customItemColumns,
    breakdownRows: [...breakdownRows, ...savedCustomBreakdownRows],
    blocks,
  };

  const tableBlock = normalized.blocks.find((block) => block.id === "items_table");
  const legacyDefaultTable = tableBlock?.x === 24 && tableBlock?.y === 228 && tableBlock?.h === 118;
  if (legacyDefaultTable) {
    normalized.blocks = normalized.blocks.map((block) => {
      if (block.id === "items_table") return { ...block, h: 62 };
      if (["fbr_invoice", "verification", "totals", "signature"].includes(block.id)) {
        return { ...block, y: Math.max(0, block.y - 56) };
      }
      return block;
    });
  }

  return normalized;
};

const getClientDesignKey = (globalData) => {
  const clientId =
    globalData?.id ||
    globalData?.user_id ||
    globalData?.client_id ||
    globalData?.ntn_cnic ||
    globalData?.email ||
    "default";

  return `${DESIGN_STORAGE_KEY}_${clientId}`;
};

export const loadLocalInvoiceDesign = (globalData) => {
  const keys = [getClientDesignKey(globalData), DESIGN_STORAGE_KEY];

  for (const key of keys) {
    try {
      const saved = JSON.parse(localStorage.getItem(key));
      if (saved?.blocks) return normalizeInvoiceDesignTemplate(saved);
    } catch {
      // Ignore invalid saved templates and fall back to the next source.
    }
  }

  return defaultInvoiceDesignTemplate;
};

export const saveLocalInvoiceDesign = (template, globalData) => {
  const normalized = normalizeInvoiceDesignTemplate(template);
  localStorage.setItem(getClientDesignKey(globalData), JSON.stringify(normalized));
  localStorage.setItem(DESIGN_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const getDesignBlock = (design, id) => {
  const blocks = Array.isArray(design?.blocks) ? design.blocks : [];
  return blocks.find((block) => block.id === id);
};

export const toPrintMm = (value, axis = "x") => {
  const canvasSize = axis === "y" ? DESIGN_CANVAS_HEIGHT : DESIGN_CANVAS_WIDTH;
  const pageSize = axis === "y" ? PRINT_PAGE_HEIGHT_MM : PRINT_PAGE_WIDTH_MM;
  return `${(((Number(value) || 0) * pageSize) / canvasSize).toFixed(2)}mm`;
};
